import {
  Accordion,
  Alert,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import { Claim } from 'interfaces';
import {
  formatClaimSourceData,
  SourceDataFile,
  useClaimSourceData,
} from './claimSourceData';

interface ClaimSourceDataModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  claim: Claim | null;
}

const renderFieldTable = (
  title: string,
  fields: Array<{ label: string; value: string }>
) => {
  if (fields.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs">
      <Title order={4}>{title}</Title>
      <Table withColumnBorders withTableBorder>
        <Table.Tbody>
          {fields.map((field) => (
            <Table.Tr key={`${title}-${field.label}`}>
              <Table.Th w="35%">{field.label}</Table.Th>
              <Table.Td>{field.value}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

const sourceLabel = (file: SourceDataFile): string => {
  if (file.kind === 'DFT') {
    return 'DFT';
  }
  return `ORU ${file.suffix}`;
};

export const ClaimSourceDataModal = ({
  open,
  setOpen,
  claim,
}: ClaimSourceDataModalProps) => {
  const { translate } = useTranslation();
  const { status, files, missing, failures } = useClaimSourceData(open, claim);

  const formatted = formatClaimSourceData(claim, files);
  const hasLoadedFiles = files.length > 0;
  const messageID = claim?.messageID ?? '';
  const siteNumber = claim?.site?.siteNumber ?? '';
  const missingDft = missing.some((entry) => entry.kind === 'DFT');
  const missingOruSuffixes = missing
    .filter((entry) => entry.kind === 'ORU')
    .map((entry) => entry.suffix)
    .filter(Boolean);

  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      title={translate('pages:claims.sourceData.modalTitle', 'Source Data')}
      size="xl"
      centered
    >
      <Stack gap="md">
        {status === 'loading' && (
          <Group>
            <Loader size="sm" />
            <Text>
              {translate(
                'pages:claims.sourceData.loading',
                'Loading source data...'
              )}
            </Text>
          </Group>
        )}

        {(status === 'partial' || status === 'error') && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            {status === 'error'
              ? translate(
                  'pages:claims.sourceData.error',
                  'Source data could not be loaded. Please try again later.'
                )
              : translate(
                  'pages:claims.sourceData.partial',
                  'Some source data could not be loaded.'
                )}
          </Alert>
        )}

        {(status === 'empty' || (status === 'error' && !hasLoadedFiles)) && (
          <Stack gap={4}>
            <Text>
              {translate(
                'pages:claims.sourceData.notFound',
                'No source data was found for this claim.'
              )}
            </Text>
            <Text size="sm" c="dimmed">
              {`Message ID: ${messageID || '-'}`}
            </Text>
            <Text size="sm" c="dimmed">
              {`Site Number: ${siteNumber || '-'}`}
            </Text>
          </Stack>
        )}

        {hasLoadedFiles && (
          <Tabs defaultValue="summary">
            <Tabs.List>
              <Tabs.Tab value="summary">Summary</Tabs.Tab>
              <Tabs.Tab value="sources">Sources</Tabs.Tab>
              <Tabs.Tab value="raw">
                {translate(
                  'pages:claims.sourceData.rawSourceData',
                  'Raw Source Data'
                )}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="summary" pt="sm">
              <Stack gap="md">
                {renderFieldTable('Summary', formatted.summary)}
                {renderFieldTable('Patient', formatted.patient)}
                {renderFieldTable('Visit / Encounter', formatted.visit)}
                {renderFieldTable('Provider', formatted.provider)}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="sources" pt="sm">
              <Stack gap="sm">
                {files.map((file) => (
                  <Stack key={file.key} gap={4}>
                    <Text fw={600}>{sourceLabel(file)}</Text>
                    <Text size="sm" c="dimmed">
                      {file.key}
                    </Text>
                  </Stack>
                ))}
                {missingDft && (
                  <Alert icon={<IconAlertCircle size={16} />} color="gray">
                    {translate(
                      'pages:claims.sourceData.dftMissing',
                      'DFT source data could not be found.'
                    )}
                  </Alert>
                )}
                {missingOruSuffixes.length > 0 && (
                  <Text size="sm" c="dimmed">
                    {translate(
                      'pages:claims.sourceData.oruOptionalMissing',
                      'Some ORU variants were not found ({{suffixes}}). This can be normal when only finalized reports are available.'
                    ).replace('{{suffixes}}', missingOruSuffixes.join(', '))}
                  </Text>
                )}
                {failures.length > 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {failures.map((failure) => (
                      <Text key={failure.key} size="sm">
                        {`${failure.kind}${failure.suffix ? ` ${failure.suffix}` : ''}: ${translate(
                          'pages:claims.sourceData.sourceFileUnreadable',
                          'This source file could not be read.'
                        )}`}
                      </Text>
                    ))}
                  </Alert>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="raw" pt="sm">
              <Accordion>
                <Accordion.Item value="raw-source-data">
                  <Accordion.Control>
                    {translate(
                      'pages:claims.sourceData.rawSourceData',
                      'Raw Source Data'
                    )}
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {files.map((file) => (
                        <Stack key={`raw-${file.key}`} gap={4}>
                          <Text fw={600}>{sourceLabel(file)}</Text>
                          <Code block>{JSON.stringify(file.data, null, 2)}</Code>
                        </Stack>
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Tabs.Panel>
          </Tabs>
        )}

        <Divider />
        <Group justify="flex-end">
          <Button variant="light" color="gray" onClick={() => setOpen(false)}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
